var _excluded = ["isInline", "isFullWidth", "direction", "align", "justify", "spacing", "wrap", "children", "divider", "className", "shouldWrapChildren"];
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { tqc, forwardRef } from '../../system';
import { cx, getValidChildren, __DEV__ } from '../../utils';
import * as React from 'react';
import { getDividerStyles, getStackStyles, selector } from './stack.utils';
export var StackDivider = props => /*#__PURE__*/React.createElement(tqc.div, _extends({
  className: "tqc-stack__divider"
}, props, {
  __css: _objectSpread(_objectSpread({}, props['__css']), {}, {
    borderWidth: 0,
    alignSelf: 'stretch',
    borderColor: 'inherit',
    width: 'auto',
    height: 'auto'
  })
}));
export var StackItem = props => /*#__PURE__*/React.createElement(tqc.div, _extends({
  className: "tqc-stack__item"
}, props, {
  __css: _objectSpread({
    display: 'inline-block',
    flex: '0 0 auto',
    minWidth: 0
  }, props['__css'])
}));
/**
 * Stacks help you easily create flexible and automatically distributed layouts
 *
 * You can stack elements in the horizontal or vertical direction,
 * and apply a space or/and divider between each element.
 *
 * It uses `display: flex` internally and renders a `div`.
 *
 */
export var Stack = forwardRef((props, ref) => {
  var {
      isInline,
      isFullWidth,
      direction: directionProp,
      align,
      justify,
      spacing = '0.5rem',
      wrap,
      children,
      divider,
      className,
      shouldWrapChildren
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  var direction = isInline ? 'row' : directionProp !== null && directionProp !== void 0 ? directionProp : 'column';
  var styles = React.useMemo(() => getStackStyles({
    direction,
    spacing
  }), [direction, spacing]);
  var dividerStyle = React.useMemo(() => getDividerStyles({
    spacing,
    direction
  }), [spacing, direction]);
  var hasDivider = !!divider;
  var shouldUseChildren = !shouldWrapChildren && !hasDivider;
  var validChildren = getValidChildren(children);
  var clones = shouldUseChildren ? validChildren : validChildren.map((child, index) => {
    var isLast = index + 1 === validChildren.length;
    var wrappedChild = /*#__PURE__*/React.createElement(StackItem, {
      key: index
    }, child);
    var _child = shouldWrapChildren ? wrappedChild : child;
    if (!hasDivider) return _child;
    var clonedDivider = /*#__PURE__*/React.cloneElement(divider, {
      __css: dividerStyle
    });
    var _divider = isLast ? null : clonedDivider;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: index
    }, _child, _divider);
  });
  var _className = cx('tqc-stack', className);
  return /*#__PURE__*/React.createElement(tqc.div, _extends({
    ref: ref,
    display: "flex",
    alignItems: align,
    justifyContent: justify,
    flexDirection: styles.flexDirection,
    flexWrap: wrap,
    className: _className,
    width: isFullWidth ? 'full' : undefined,
    __css: hasDivider ? {} : {
      [selector]: styles[selector]
    }
  }, rest), clones);
});
if (__DEV__) {
  Stack.displayName = 'Stack';
}

/**
 * A view that arranges its children in a horizontal line.
 */
export var HStack = forwardRef((props, ref) => /*#__PURE__*/React.createElement(Stack, _extends({
  align: "center"
}, props, {
  direction: "row",
  ref: ref
})));
if (__DEV__) {
  HStack.displayName = 'HStack';
}

/**
 * A view that arranges its children in a vertical line.
 */
export var VStack = forwardRef((props, ref) => /*#__PURE__*/React.createElement(Stack, _extends({
  align: "center"
}, props, {
  direction: "column",
  ref: ref
})));
if (__DEV__) {
  VStack.displayName = 'VStack';
}